/* Progressive enhancement for the public header's native disclosures. */
if (!customElements.get('yatdim-header')) {
  customElements.define('yatdim-header', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.menu = this.querySelector('.yd-header__menu');
      this.disclosures = [...this.querySelectorAll('.yd-nav__disclosure')];
      this.mobile = window.matchMedia('(max-width: 1199px)');
      this.addEventListener('keydown', (event) => {
        if (event.key === ' ' && this.openCart(event)) return;
        if (event.key !== 'Escape') return;
        const disclosure = event.target.closest('details[open]');
        if (!disclosure || !this.contains(disclosure)) return;
        event.preventDefault();
        event.stopPropagation();
        disclosure.open = false;
        disclosure.querySelector('summary')?.focus();
      }, options);
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeMenus();
      }, options);
      this.addEventListener('focusout', (event) => {
        if (!event.relatedTarget) return;
        for (const disclosure of this.disclosures) {
          if (disclosure.open && !disclosure.contains(event.relatedTarget)) disclosure.open = false;
        }
        if (!this.contains(event.relatedTarget)) this.closeMenus();
      }, options);
      this.addEventListener('click', (event) => {
        this.openCart(event);
        if (event.target.closest('a')) this.closeMenus();
      }, options);
      this.addEventListener('toggle', (event) => {
        const current = event.target;
        if (!current.open || !current.classList.contains('yd-nav__disclosure')) return;
        for (const disclosure of this.disclosures) {
          if (disclosure !== current && !disclosure.contains(current) && !current.contains(disclosure)) disclosure.open = false;
        }
        this.positionPanel(current);
      }, { ...options, capture: true });
      this.mobile.addEventListener('change', () => {
        const focused = document.activeElement;
        this.closeMenus();
        if (this.contains(focused) && !focused.getClientRects().length) {
          (this.mobile.matches ? this.menu?.querySelector('summary') : this.querySelector('.yd-header__logo'))?.focus();
        }
        this.updateHeight();
      }, options);
      window.addEventListener('resize', () => this.scheduleHeight(), options);
      window.addEventListener('scroll', () => this.scheduleHeight(), { ...options, passive: true });
      document.addEventListener('DOMContentLoaded', () => this.updateHeight(), options);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.updateHeight());
        this.observer.observe(this);
        for (const section of document.querySelectorAll('.shopify-section-group-header-group')) this.observer.observe(section);
      }
      this.updateHeight();
    }

    /** Maintain the theme offsets consumed by product, filters and cart panels. */
    updateHeight() {
      const root = document.documentElement.style;
      const sticky = this.classList.contains('yd-header--sticky');
      const menuTop = Math.max(0, this.getBoundingClientRect().bottom);
      const end = sticky ? menuTop : 0;
      const paddedEnd = end + (window.matchMedia('(min-width: 769px)').matches ? 56 : 20);
      root.setProperty('--yd-sticky-height', `${sticky ? this.offsetHeight : 0}px`);
      root.setProperty('--yd-menu-top', `${menuTop}px`);
      root.setProperty('--header-height', `${this.offsetHeight}px`);
      root.setProperty('--header-end', `${end}px`);
      root.setProperty('--header-end-padded', `${paddedEnd}px`);
      const content = document.getElementById('main-content');
      if (content) root.setProperty('--content-start', `${content.getBoundingClientRect().top + window.scrollY}px`);
    }

    scheduleHeight() {
      if (this.heightFrame) return;
      this.heightFrame = requestAnimationFrame(() => {
        this.heightFrame = null;
        this.updateHeight();
      });
    }

    positionPanel(disclosure) {
      const panel = disclosure.querySelector(':scope > .yd-nav__panel');
      if (!panel || this.mobile.matches || disclosure.closest('.yd-nav__panel')) return;
      panel.classList.remove('yd-nav__panel--align-end');
      const bounds = panel.getBoundingClientRect();
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (rtl ? bounds.left < 20 : bounds.right > window.innerWidth - 20) panel.classList.add('yd-nav__panel--align-end');
    }

    /** Also works after the theme editor replaces only the header section. */
    openCart(event) {
      if (event.defaultPrevented || !event.target.closest('.yd-header__cart')) return false;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return false;
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: event.target.closest('.yd-header__cart') } }));
      return true;
    }

    closeMenus() {
      if (this.menu) this.menu.open = false;
      for (const disclosure of this.disclosures) disclosure.open = false;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.heightFrame);
      for (const name of ['--yd-sticky-height', '--yd-menu-top', '--header-height', '--header-end', '--header-end-padded', '--content-start']) document.documentElement.style.removeProperty(name);
    }
  });
}

/* Keep the native form available until automatic language switching is ready. */
if (!customElements.get('yatdim-localization')) {
  customElements.define('yatdim-localization', class extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      const language = form?.querySelector('select[name="language_code"]');
      if (!form || !language) return;

      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const dropdown = form.querySelector('.yd-localization__dropdown');
      const native = form.querySelector('.yd-localization__native');
      if (dropdown && native) {
        const trigger = dropdown.querySelector('summary');
        const choices = [...dropdown.querySelectorAll('[data-language-value]')];
        dropdown.addEventListener('click', (event) => {
          const choice = event.target.closest('[data-language-value]');
          if (!choice || !dropdown.contains(choice)) return;
          const changed = language.value !== choice.dataset.languageValue;
          language.value = choice.dataset.languageValue;
          const flag = choice.querySelector('.yd-localization__flag');
          dropdown.querySelector('[data-current-flag]').replaceChildren(...[...flag.childNodes].map(node => node.cloneNode(true)));
          const name = dropdown.querySelector('[data-current-language]');
          name.textContent = choice.querySelector('.yd-localization__option-name').textContent;
          name.lang = language.value;
          for (const item of choices) {
            if (item === choice) item.setAttribute('aria-current', 'true');
            else item.removeAttribute('aria-current');
          }
          dropdown.open = false;
          trigger.focus();
          if (changed) language.dispatchEvent(new Event('change', { bubbles: true }));
        }, options);
        dropdown.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && dropdown.open) {
            event.preventDefault();
            event.stopPropagation();
            dropdown.open = false;
            trigger.focus();
            return;
          }
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          if (!dropdown.open && !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
          event.preventDefault();
          dropdown.open = true;
          const current = choices.indexOf(document.activeElement);
          let next = (current + 1) % choices.length;
          if (event.key === 'ArrowUp') next = current < 0 ? choices.length - 1 : (current - 1 + choices.length) % choices.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = choices.length - 1;
          choices[next]?.focus();
        }, options);
        dropdown.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !dropdown.contains(event.relatedTarget)) dropdown.open = false;
        }, options);
        document.addEventListener('click', (event) => {
          if (!dropdown.contains(event.target)) dropdown.open = false;
        }, options);
        dropdown.hidden = false;
        native.hidden = true;
      }
      form.addEventListener('submit', (event) => {
        const previewUrl = this.#localLanguageUrl(form, language);
        if (previewUrl) {
          event.preventDefault();
          window.location.assign(previewUrl.href);
          return;
        }
        const returnTo = form.querySelector('input[name="return_to"]');
        if (returnTo) returnTo.value = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      }, options);
      if (!this.hasAttribute('data-auto-submit') || typeof form.requestSubmit !== 'function') return;
      language.addEventListener('change', () => form.requestSubmit(), options);
      const button = form.querySelector('button[type="submit"]');
      if (button) button.hidden = true;
      const hint = form.querySelector('.yd-localization__hint');
      if (hint) hint.hidden = false;
    }

    /** The local CLI renderer supports language paths even when its form proxy rejects authentication. */
    #localLanguageUrl(form, language) {
      if (!['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)) return null;
      if (window.Shopify?.theme?.role !== 'development' || form.querySelector('[name="country_code"]')) return null;
      const languageRoot = language.selectedOptions[0]?.dataset.rootUrl;
      if (!languageRoot?.startsWith('/') || languageRoot.startsWith('//')) return null;

      const url = new URL(window.location.href);
      const currentRoot = (this.dataset.languageRoot || '/').replace(/\/+$/, '');
      let path = url.pathname;
      if (currentRoot) {
        const currentPath = path.toLowerCase();
        const rootPath = currentRoot.toLowerCase();
        if (currentPath === rootPath) path = '/';
        else if (currentPath.startsWith(`${rootPath}/`)) path = path.slice(currentRoot.length);
      }
      url.pathname = `${languageRoot.replace(/\/+$/, '')}${path}`;
      return url;
    }

    disconnectedCallback() {
      this.controller?.abort();
    }
  });
}
